/**
 * Henvendelsen slik BYRÅET ser den, og svaret de gir (migrering 0086).
 *
 * ---------------------------------------------------------------------------------------------
 * Vedtak 94: bud og forespørsler speiles — hver part eier sitt eksemplar. Festivalens er
 * `BookingBidDto`; dette er byråets, og de to har med vilje ikke samme form. Et bud bærer tjueto
 * vilkårskolonner som svarer på «hva tilbyr DERE»; en henvendelse svarer på «hva spør de om».
 *
 * ---------------------------------------------------------------------------------------------
 * SVARET ER TRE TING SAMTIDIG, og det er derfor det ikke er ett felt.
 *
 * Vedtak 99: «ledig / opptatt / hold». Et HOLD er bransjens option — en midlertidig reservasjon med
 * en dato den løper ut. Uten den svarer byrået bare med et tall, og datoen må avklares på telefon
 * likevel.
 *
 * `unknown` er tilstanden FØR noen har svart, og den er ikke det samme som `busy`. En liste som
 * blandet de to ville sagt at artisten er opptatt overalt vi ennå ikke har spurt.
 */
import { z } from 'zod';
import { engagementRefDtoSchema } from './engagements.js';

import { CONCERT_DEAL_TYPES } from '../concerts/deal-math.js';
import { bookingBidKindSchema } from '../enums.js';
import { bidTermsSchema } from './booking-terms.js';
import { cursorQuerySchema, page } from '../http/pagination.js';

/** Hvor langt henvendelsen er kommet HOS BYRÅET. Festivalens bud har sin egen status. */
/**
 * MOTBUD ER EN EGEN TILSTAND (sak 110.1, migrering 0150).
 *
 * Atle, 11. september: «Jobben til innboksen er å vise hvem ballen ligger hos. Hvis en rad står
 * som «Svart» mens det egentlig ligger et motbud og venter på byrået, er det nettopp den typen
 * feil som gjør at en agent glemmer å svare.»
 *
 * `countered` sier at festivalen har lagt et annet tall på bordet. Den er ikke `new` — byrået HAR
 * svart — og den er ikke `answered`, for da ville raden sett ferdig ut.
 */
export const ENQUIRY_STATUSES = [
  'new',
  'answered',
  'countered',
  'accepted',
  'declined',
  'withdrawn',
] as const;

/**
 * DE TO TILSTANDENE DER BALLEN LIGGER HOS BYRÅET.
 *
 * Skrevet ut her og ikke utledet på skjermen, fordi både filteret i API-et og pillen i
 * innboksen må mene det samme — og et tall som er feil på en måte ingen kan se er verre enn
 * intet tall.
 */
export const ENQUIRY_WAITING_ON_US = ['new', 'countered'] as const;
export type EnquiryStatus = (typeof ENQUIRY_STATUSES)[number];
export const enquiryStatusSchema = z.enum(ENQUIRY_STATUSES).meta({ id: 'EnquiryStatus' });

export const ENQUIRY_AVAILABILITIES = ['unknown', 'free', 'busy', 'hold'] as const;
export type EnquiryAvailability = (typeof ENQUIRY_AVAILABILITIES)[number];
export const enquiryAvailabilitySchema = z
  .enum(ENQUIRY_AVAILABILITIES)
  .meta({ id: 'EnquiryAvailability' });

export const bookingEnquiryDtoSchema = z
  .object({
    id: z.uuid(),
    /** Hvem som spør. Navnet er en kopi tatt da de spurte — sannheten bor hos dem, bak RLS. */
    counterpartyName: z.string(),
    counterpartyOrganizationId: z.uuid(),

    /** Tekst, ikke en peker: byrået har ingen egen roster ennå (vedtak 98.1). */
    artistName: z.string(),
    bookingArtistId: z.uuid().nullable(),
    /**
     * VILKÅRENE I BUDET DE SKAL SVARE PÅ (QA-punkt 25, migrering 0160).
     *
     * Rapporten: «Byrået ser heller ikke vilkårene (gjesteliste, eksklusivitet, billing) i
     * budmodalen før de godtar.» Et godtatt bud blir en kontrakt med tjueto vilkårskolonner, og
     * byrået trykket «Godta» på et beløp og en dato.
     *
     * LEST GJENNOM EN PORT, IKKE SPEILET. Vilkårene endrer seg mens forhandlingen går — 0128
     * speiler beløpene av nettopp den grunnen — og en kopi ville vært utdatert i det øyeblikket
     * festivalen rettet en setning. Da hadde byrået lest en annen avtale enn den de godtok.
     *
     * Null når budet er borte, eller når henvendelsen ikke kom fra et bud i det hele tatt.
     */
    terms: bidTermsSchema.nullable(),
    /**
     * STØRRELSEN FORESPØRSELEN GJELDER (0115, ARTISTS.md §5.2).
     *
     * Navnet følger med framfor bare id-en: hver eneste leser av dette feltet skal SKRIVE navnet
     * på skjermen — under artistnavnet i lista, i «venter svar» på oversikten, i formatkolonnen i
     * bookingene — og et oppslag per rad mot en liste klienten ikke har er tre oppslag for
     * ingenting. Navnet er en kopi av formatets navn slik det står nå, ikke slik det sto da noen
     * spurte: retter byrået en skrivefeil i «Fullt bnad», retter den seg her også.
     *
     * BEGGE ER NULL NÅR INGEN HAR VALGT. En festival som spør «kan de 16. juli?» har ofte ikke
     * bestemt størrelsen ennå, og det er forhandlingen som skal avgjøre den.
     */
    bookingArtistFormatId: z.uuid().nullable(),
    formatName: z.string().nullable(),

    kind: bookingBidKindSchema,
    amountMinor: z.number().int().nullable(),
    currency: z.string(),
    /**
     * AVTALETYPEN I BUDET, SOM EGET FELT (sak 222.43, 0444). Før sto den bare i åpningsmeldingen.
     * Lest gjennom vilkårsporten, som resten av budet. Null når budet bare sier beløpet.
     */
    dealType: z.enum(CONCERT_DEAL_TYPES).nullable(),
    dealArtistPercent: z.number().nullable(),
    dealAfterCosts: z.boolean().nullable(),
    performanceDate: z.iso.date().nullable(),
    venueName: z.string().nullable(),
    /**
     * SCENEN OG KLOKKESLETTET (QA runde 7, punkt 9).
     *
     * `venueName` er ARRANGEMENTETS navn — speilingen skriver `festival_name` dit. Scenen og tida
     * sto bare i budmeldingen i chatten, som betyr at en agent måtte lete nedover i en samtale for
     * å finne de to opplysningene svaret deres avhenger av.
     *
     * Begge kommer fra `app.bid_terms_for_enquiry` (0182), altså fra BUDET og ikke fra en kolonne
     * på henvendelsen: det er festivalens programmering, og den eier vi ikke.
     *
     * Null er et ekte svar på begge: et bud som ble sendt før artisten var satt på brettet har
     * ingen scene, og et uten klokkeslett har bare en dato.
     */
    stageName: z.string().nullable(),
    startsAtTime: z.string().nullable(),
    respondsBy: z.iso.date().nullable(),
    message: z.string().nullable(),

    status: enquiryStatusSchema,

    availability: enquiryAvailabilitySchema,
    holdUntil: z.iso.date().nullable(),
    askingFeeMinor: z.number().int().nullable(),
    answeredAt: z.iso.datetime().nullable(),
    answerNote: z.string().nullable(),
    /**
     * FORMAT OG ANTALL REISENDE I «SVARET VÅRT» (0945, QA13 90). Egne felt: `bookingArtistFormatId`
     * er det arrangøren spurte om, dette er det byrået svarte. Arrangøren leser dem gjennom saken.
     */
    answerFormatId: z.uuid().nullable(),
    answerFormatName: z.string().nullable(),
    answerTravellingParty: z.number().int().nullable(),

    /**
     * Hvem som SVARER. Aldri hvem som får lese.
     *
     * Atle, 8. september: «Egentlig at agenten som representerer artisten, men de andre kan se alt
     * av bud og forespørsler, det er ikke skjult.» Ingen spørring filtrerer på dette feltet.
     */
    assigneePersonId: z.uuid().nullable(),
    assigneeName: z.string().nullable(),
    /**
     * DEN SOM FAKTISK VARSLES (sak 118).
     *
     * Forespørselens egen ansvarlige når den er satt, ellers ARTISTENS. Er begge tomme, varsles
     * ingen — og da er denne null, som er et ærlig svar og ikke en mangel.
     *
     * Skjermen viser denne, og `assigneePersonId` er fortsatt det man SKRIVER. To felter fordi de
     * svarer på to spørsmål: «hvem er satt på akkurat denne» og «hvem får beskjed».
     */
    notifyPersonId: z.uuid().nullable(),
    notifyName: z.string().nullable(),

    /**
     * NÅR BYRÅET LA DEN BORT (0117, ARTISTS.md §3.2).
     *
     * Den står i svaret framfor å bare filtrere bort raden, fordi dialogen skal kunne si «denne er
     * arkivert» for den som åpner den gjennom en lenke. Innboksen skjuler dem; en direkte lenke
     * gjør det ikke.
     */
    archivedAt: z.iso.datetime().nullable(),

    /**
     * NÅR BYRÅET DELTE REISEFØLGET SELV (0389, sak 212). Da får arrangøren hente reisefølget selv om
     * avtalen ble gjort utenfor Tvitvi. Delingen trekkes ikke tilbake: det arrangøren alt har hentet,
     * er hentet.
     */
    partySharedAt: z.iso.datetime().nullable(),
    partySharedByName: z.string().nullable(),

    createdAt: z.iso.datetime(),
    /** Engasjementet raden hører til (0927, grep F1): den ene statusen alle flatene leser. */
    engagement: engagementRefDtoSchema.nullable().optional(),
  })
  .meta({ id: 'BookingEnquiryDto' });

/**
 * Å BYTTE STØRRELSE PÅ EN FORESPØRSEL (0115, ARTISTS.md §3.2).
 *
 * `null` er et ekte valg: en festival som har ombestemt seg om hvilken størrelse de vil ha, kan
 * settes tilbake til «ikke bestemt» framfor å måtte peke på et format ingen har blitt enige om.
 */
export const setEnquiryFormatRequestSchema = z
  .object({ bookingArtistFormatId: z.uuid().nullable() })
  .meta({ id: 'SetEnquiryFormatRequest' });

/** Å legge en forespørsel bort, eller hente den fram igjen. Ett felt, begge veier. */
export const archiveEnquiryRequestSchema = z
  .object({ archived: z.boolean() })
  .meta({ id: 'ArchiveEnquiryRequest' });

export type SetEnquiryFormatRequest = z.infer<typeof setEnquiryFormatRequestSchema>;
export type ArchiveEnquiryRequest = z.infer<typeof archiveEnquiryRequestSchema>;
export type BookingEnquiryDto = z.infer<typeof bookingEnquiryDtoSchema>;

export const ENQUIRY_SORT_KEYS = ['created', 'respondsBy'] as const;
export type EnquirySortKey = (typeof ENQUIRY_SORT_KEYS)[number];

export const listBookingEnquiriesQuerySchema = cursorQuerySchema(ENQUIRY_SORT_KEYS)
  .extend({
    status: enquiryStatusSchema.optional(),
    /** «Venter på oss» — se `ENQUIRY_WAITING_ON_US`. Sann eller utelatt; aldri usann. */
    waitingOnUs: z.coerce.boolean().optional(),
    /** Fritekst mot artistnavn og hvem som spør — det man husker når man leter. */
    search: z.string().trim().min(1).max(120).optional(),
    /**
     * BARE DE ARKIVERTE (QA5 E #5). Uten den var en arkivert forespørsel borte for godt: ingen
     * liste viste den, og «Gjenopprett» sto i en meny ingen kom til. Sann eller utelatt, som
     * `waitingOnUs`.
     */
    archived: z.coerce.boolean().optional(),
    /**
     * ARRANGØREN (F24, Atle 04.10): bare forespørslene fra én arrangør, ved navnet raden bærer.
     * Navnet og ikke en id: en arrangør utenfor Tvitvi har bare navnet (vedtak 98.1).
     */
    organizer: z.string().trim().min(1).max(200).optional(),
    /**
     * SKJUL «FALT BORT» (QA13 102): sakene som falt bort, står ikke i lista før man ber om dem.
     * Sann eller utelatt; utelatt gir alt, som før.
     */
    hideFellThrough: z.coerce.boolean().optional(),
    /**
     * ÉN ARTIST I REGISTERET (251.5): artistsiden henter forespørslene til artisten selv, alle sider,
     * i stedet for første side av hele innboksen. Utelatt gir alle, som før.
     */
    artistId: z.uuid().optional(),
  })
  .meta({ id: 'ListBookingEnquiriesQuery' });
export type ListBookingEnquiriesQuery = z.infer<typeof listBookingEnquiriesQuerySchema>;

export const bookingEnquiryListResponseSchema = page(
  bookingEnquiryDtoSchema,
  'BookingEnquiryListResponse',
);
export type BookingEnquiryListResponse = z.infer<typeof bookingEnquiryListResponseSchema>;

/** Arrangørene innboksen har forespørsler fra, til filteret (F24). Alfabetisk, med antall. */
export const enquiryOrganizersResponseSchema = z
  .object({
    items: z.array(z.object({ name: z.string(), count: z.number().int().nonnegative() })),
  })
  .meta({ id: 'EnquiryOrganizersResponse' });
export type EnquiryOrganizersResponse = z.infer<typeof enquiryOrganizersResponseSchema>;

/**
 * Svaret.
 *
 * `holdUntil` er PÅKREVD når svaret er `hold` og forbudt ellers — skranken i basen sier det samme.
 * Et hold uten en dato er ikke et hold, det er et løfte ingen kan følge opp.
 */
export const answerBookingEnquiryRequestSchema = z
  .object({
    availability: enquiryAvailabilitySchema,
    holdUntil: z.iso.date().nullish(),
    /** Asking fee. Byråets tall, ikke festivalens bud — de to møtes i forhandlingen. */
    askingFeeMinor: z.number().int().nonnegative().max(100_000_000_00).nullish(),
    note: z.string().trim().min(1).max(2000).nullish(),
    /**
     * SVARET, SLIK DET SKAL STÅ I SAMTALEN.
     *
     * Atle, 9. september: «Når en artist får en forespørsel skal de svare og bekrefte i "samme"
     * chat som arrangøren sendte forespørselen i.» Feltene over er STRUKTUR — de styrer kalenderen,
     * fargene og prisen — men en arrangør som venter på svar ser på tråden, og et svar som bare
     * endret fire kolonner ville vært et svar ingen fikk.
     *
     * TEKSTEN KOMMER FRA KLIENTEN, og det er ikke latskap: API-et returnerer aldri en oversatt
     * setning, og det skal heller ikke skrive en. Klienten har språkfilene og vet om leseren
     * skriver nynorsk eller engelsk; serveren vet bare hva som ble avtalt.
     *
     * VALGFRITT, fordi feltene over er svaret og dette er kunngjøringen av det. En integrasjon som
     * bare setter tilgjengelighet er fortsatt et lovlig svar.
     */
    message: z.string().trim().min(1).max(4000).optional(),
    /**
     * SVARET PÅ ET BUD, som er et annet spørsmål enn svaret på en forespørsel.
     *
     * ---------------------------------------------------------------------------------------
     * Atle, 11. september: «når et bud kommer inn er det ikke tydelig markert det er et bud. I
     * tillegg er det ikke mulighet da for å sende motbud eller akseptere det. Det er kun mulighet
     * for å svare på en forespørsel.»
     *
     * En FORESPØRSEL spør «er dere ledige, hva koster dere» — og svaret er tilgjengelighet og en
     * pris. Et BUD er et tilbud på bordet: 120 000 for 16. juli. Det svarer man på ved å GODTA,
     * gi et MOTBUD, eller AVSLÅ. Dialogen tilbød bare det første spørsmålets svar, uansett hva
     * som faktisk lå der.
     *
     * DE TRE ER IKKE EN NY TILSTAND, men en presis lesing av de feltene som allerede finnes:
     * `accept` er ledig til DERES pris, `counter` er ledig til VÅR, `decline` er opptatt. Det som
     * manglet var at serveren visste hvilken av de tre det var — «ledig til den samme prisen» og
     * «vi godtar» er umulig å skille på feltene alene, og bare den siste skal bekrefte bookingen.
     */
    /*
     * `counter` er borte (AI-ANALYSE §8.1 #10): et motbud er en versjon på avtalearket (C3), og
     * tjenesten avviste den alltid. Nå sier kontrakten det samme som tjenesten.
     */
    decision: z.enum(['accept', 'decline']).optional(),
    /**
     * FORMATET OG ANTALLET SVARET GJELDER (0945, QA13 90). Utelatt rører ikke det som står; null
     * tømmer. Formatet må være byråets eget format på artisten.
     */
    answerFormatId: z.uuid().nullable().optional(),
    answerTravellingParty: z.number().int().min(0).max(500).nullable().optional(),
  })
  .refine((value) => (value.availability === 'hold') === (value.holdUntil != null), {
    path: ['holdUntil'],
    message: 'A hold needs a date, and a date needs a hold.',
  })
  .meta({ id: 'AnswerBookingEnquiryRequest' });
export type AnswerBookingEnquiryRequest = z.infer<typeof answerBookingEnquiryRequestSchema>;

/** Hvem som svarer. `null` tar tildelingen tilbake, og lar henvendelsen være hele husets. */
export const assignBookingEnquiryRequestSchema = z
  .object({ assigneePersonId: z.uuid().nullable() })
  .meta({ id: 'AssignBookingEnquiryRequest' });
export type AssignBookingEnquiryRequest = z.infer<typeof assignBookingEnquiryRequestSchema>;

/**
 * ÉN ADVANCE, DELT AV TO HUS (0118, ARTISTS.md §4.1, vedtak 104 og 105).
 *
 * ---------------------------------------------------------------------------------------------
 * SVARET ER SKREVET FOR DEN SOM SPØR, ikke for tabellen.
 *
 * `startedByUs` og `counterpartyName` er de to som gjør at skjermen kan skrive setningen §4.1 ber
 * om — «{Organiser} has already started the advance for this booking» — uten å sammenlikne
 * organisasjons-id-er i en komponent. Serveren vet hvem som spør; klienten skal slippe.
 *
 * `needsPicker` er per HUS og ikke per advance (§4.4): den som kom sist skal også få tatt med sitt
 * eget, og «arriving second never costs an organisation the chance to bring its own people».
 */
export const sharedAdvanceDtoSchema = z
  .object({
    id: z.uuid(),
    bookingEnquiryId: z.uuid(),
    /** Festivalens programpost når den finnes. Null når byrået lagde advancen først. */
    performanceId: z.uuid().nullable(),
    /**
     * VEIEN TIL PROGRAMPOSTEN, for den som eier den.
     *
     * Advancen ER programposten (0133), og festivalens egen adresse til den går gjennom festival
     * og årgang. Byrået bruker sin egen sti — `/artists/:artistId/advance/:performanceId` — så de
     * to feltene er null for dem, og det er riktig: de har ingen lineup å gå tilbake til.
     */
    performanceEditionId: z.uuid().nullable(),
    performanceFestivalId: z.uuid().nullable(),
    startedByName: z.string(),
    startedByUs: z.boolean(),
    counterpartyName: z.string(),
    needsPicker: z.boolean(),
    /**
     * ARRANGEMENTET — festivalen, eller konsertens spillested (K4 F11, 0957). Overskriften er
     * «Artist @ Arrangement»; motpartens organisasjonsnavn sa ikke hvilken festival det gjaldt.
     * Null før programposten finnes.
     */
    eventName: z.string().nullable().optional(),
    /**
     * ENGASJEMENTET, så byrået finner den felles kontrakten før programposten finnes (274.7: kontrakten
     * lages når budet godtas, programposten først ved «Flytt over i programmet»).
     */
    engagementId: z.uuid().nullable().optional(),
  })
  .meta({ id: 'SharedAdvance' });

export type SharedAdvanceDto = z.infer<typeof sharedAdvanceDtoSchema>;

/**
 * HVA PLUKKEREN TAR MED INN (§4.4).
 *
 * Tomme lister er lovlig — «Bring nothing for now» er et gyldig svar. Innholdet havner i
 * PROGRAMPOSTENS egne tabeller (0133): rideren blir en lenke i advance-fanen festivalen allerede
 * leser. Det er hele poenget — ett objekt, ikke to.
 */
export const enterAdvanceRequestSchema = z
  .object({
    memberIds: z.array(z.uuid()).max(200).default([]),
    documentIds: z.array(z.uuid()).max(200).default([]),
    contactIds: z.array(z.uuid()).max(200).default([]),
  })
  .meta({ id: 'EnterAdvanceRequest' });
export type EnterAdvanceRequest = z.infer<typeof enterAdvanceRequestSchema>;

/**
 * HVILKE AV HUSETS DOKUMENTER SOM FØLGER ADVANCEN (§4.4, vedtak 113.2).
 *
 * ------------------------------------------------------------------------------------------------
 * Atle svarte «113.2 står — setning + dokumentgruppe». Festivalen slipper plukkeren fordi advancen
 * ER programposten deres, men det er ÉN ting §4.4 nevner som likevel ligger utenfor den: husets
 * egne dokumenter — scenekart, get-in, sikkerhet, akkreditering. De bor i Dokumenter, ikke på
 * acten, og ingen andre veier dit for motparten.
 *
 * ÉN RAD PER FIL, IKKE PER MAPPE. Mappa er en sortering i vårt hus; byrået skal ha scenekartet, og
 * det spiller ingen rolle for dem om det lå under «Produksjon» eller «Sikkerhet». Mappenavnet står
 * i raden fordi det er slik man kjenner fila igjen, ikke fordi det betyr noe der den havner.
 */
/**
 * De tre seksjonene en advance har, som et husdokument kan høre hjemme i.
 *
 * Den samme lista `SECTION_FOR_KIND` ruter byråets filer til. Husets dokumenter fikk den i vedtak
 * O-6, fordi alt gikk til Teknisk uansett hva det var — «et parkeringskart og en HMS-rutine er ikke
 * teknisk».
 */
export const ADVANCE_SECTION_KEYS = ['technical', 'hospitality', 'promo'] as const;
export type AdvanceSectionKey = (typeof ADVANCE_SECTION_KEYS)[number];
export const advanceSectionKeySchema = z
  .enum(ADVANCE_SECTION_KEYS)
  .meta({ id: 'AdvanceSectionKey' });

export const advanceHouseDocumentSchema = z
  .object({
    attachmentId: z.uuid(),
    filename: z.string(),
    contentType: z.string(),
    byteSize: z.number().int(),
    /** Mappa den ligger i hos oss — gjenkjenningen, ikke en adresse. */
    folderName: z.string(),
    /** Årgangen mappa hører til, eller null når den ligger i husets egen sone. */
    editionName: z.string().nullable(),
    /** Om den alt er kopiert inn i advancen. Avgjort på pekeren fra 0164, ikke på filnavnet. */
    following: z.boolean(),
    /**
     * HVILKEN SEKSJON DEN FØLGER ACTEN INN I — og om den gjelder enhver act i det hele tatt.
     *
     * NULL betyr «hører til én artist eller én anledning»: den står ikke i plukkeren med mindre man
     * ber om å se alle. Se vedtak O-6 og migrering 0192.
     */
    advanceSection: advanceSectionKeySchema.nullable(),
  })
  .meta({ id: 'AdvanceHouseDocument' });
export type AdvanceHouseDocument = z.infer<typeof advanceHouseDocumentSchema>;

export const advanceHouseDocumentListResponseSchema = z
  .object({ items: z.array(advanceHouseDocumentSchema) })
  .meta({ id: 'AdvanceHouseDocumentListResponse' });
export type AdvanceHouseDocumentListResponse = z.infer<
  typeof advanceHouseDocumentListResponseSchema
>;

/**
 * HELE SETTET, IKKE ET TILLEGG.
 *
 * En fil som er huket av følger advancen; en som ikke er det, følger den ikke. Å sende settet
 * framfor «legg til denne» gjør at avhukingen betyr noe — og den må bety noe, ellers er det
 * nøyaktig avkryssingen uten konsekvens som 113.3 ble avvist for.
 */
export const setAdvanceHouseDocumentsRequestSchema = z
  .object({ attachmentIds: z.array(z.uuid()).max(200).default([]) })
  .meta({ id: 'SetAdvanceHouseDocumentsRequest' });
export type SetAdvanceHouseDocumentsRequest = z.infer<typeof setAdvanceHouseDocumentsRequestSchema>;
